import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setReadOnlyMode } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Global 402 handler — any business API call rejected because the trial has
  // lapsed re-hydrates the auth context so ProtectedRoute can paywall the user.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 402) {
          const trial = err.response.data?.trial;
          if (trial) {
            setUser((prev) => (prev ? { ...prev, trial } : prev));
          } else {
            // Refresh from /auth/me to pick up the latest trial payload.
            checkAuth();
          }
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Non-fatal: server may already have expired the session.
    }
    setUser(null);
  }, []);

  // A lapsed trial is read only (not locked out) only while the server-side
  // switch is on — the server decides, we just reflect it.
  const readOnly = !!user?.trial?.read_only;
  const trialActive = !user?.trial || user.trial.active || user.trial.exempt || readOnly;

  useEffect(() => {
    setReadOnlyMode(readOnly);
  }, [readOnly]);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, trialActive, readOnly }),
    [user, loading, checkAuth, logout, trialActive, readOnly],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
